import React from "react";
import { CheckCircle2, Circle, Package, PackageCheck, Truck, Home, CreditCard } from "lucide-react";

/**
 * OrderTimeline — visual fulfilment progress for a buyer's order.
 *
 * Stages (in order):
 *   awaiting_payment → preparing → packed → shipped → delivered
 *
 * Cancelled/failed payments collapse to a single error chip and skip the timeline.
 */
const STAGES = [
  { key: "awaiting_payment", label: "Ordered",        icon: CreditCard },
  { key: "preparing",        label: "Payment confirmed", icon: CheckCircle2 },
  { key: "packed",           label: "Packed",         icon: Package },
  { key: "shipped",          label: "Shipped",        icon: Truck },
  { key: "delivered",        label: "Delivered",      icon: Home },
];

const STAGE_INDEX = STAGES.reduce((acc, s, i) => { acc[s.key] = i; return acc; }, {});

const OrderTimeline = ({ stage = "preparing", paidAt, shippedAt, deliveredAt, trackingUrl, compact = false }) => {
  const idx = STAGE_INDEX[stage] ?? 0;
  return (
    <div className={`relative ${compact ? "" : "py-2"}`} data-testid="order-timeline">
      <div className="flex items-start gap-0">
        {STAGES.map((s, i) => {
          const done = i < idx;
          const current = i === idx;
          const Icon = s.icon;
          return (
            <React.Fragment key={s.key}>
              <div className="flex flex-col items-center flex-1 min-w-0">
                <div
                  className={[
                    "w-9 h-9 rounded-full grid place-items-center shrink-0 transition-colors",
                    done ? "bg-[#2A3D36] text-white" : "",
                    current ? "bg-[#C05A3C] text-white ring-4 ring-[#C05A3C]/20" : "",
                    !done && !current ? "bg-[#F3EFE9] text-muted-foreground" : "",
                  ].join(" ")}
                  data-testid={`timeline-node-${s.key}`}
                >
                  {done ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                </div>
                <div className={`mt-2 text-[10px] sm:text-xs text-center leading-tight ${current ? "font-semibold text-[#1A1A1A]" : "text-muted-foreground"}`}>
                  {s.label}
                </div>
              </div>
              {i < STAGES.length - 1 && (
                <div className={`flex-1 h-[2px] mt-[18px] ${i < idx ? "bg-[#2A3D36]" : "bg-black/10"}`} aria-hidden />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {trackingUrl && stage === "shipped" && (
        <div className="mt-3 text-xs">
          <a href={trackingUrl} target="_blank" rel="noopener noreferrer" className="text-[#C05A3C] hover:underline font-semibold" data-testid="timeline-tracking-link">
            Track your parcel →
          </a>
        </div>
      )}
    </div>
  );
};

export const stageLabel = (stage) => STAGES.find((s) => s.key === stage)?.label || stage;

export default OrderTimeline;
